import React, { useState, useEffect, useRef, useCallback } from 'react';
import Wallet from './Wallet';
import LoyaltyWallet from './LoyaltyWallet';
import { hapticTick } from '../../_utils/haptics';
import './Wallet.css';
import './WalletHub.css';

// WalletHub — the customer's Wallet area, reached from the bottom "Wallet" tab.
// One header, two sub-tabs: Wallet (gift cards) and Loyalty (stamp/points
// cards). The sub-tab IS the route (/dashboard/wallet vs /dashboard/loyalty):
// switching replaces the URL, so back-button behaviour and the Account
// menu's deep links stay consistent. Swipe horizontally to switch, same
// axis-locked gesture as the marketplace category feed.
const TABS = ['wallet', 'loyalty'];

const WalletHub = ({ history, location }) => {
  const initial = (location && location.pathname && location.pathname.indexOf('loyalty') !== -1) ? 'loyalty' : 'wallet';
  const [tab, setTab] = useState(initial);
  const [dragX, setDragX] = useState(0);
  const touchRef = useRef({ x: 0, y: 0, axis: null, active: false });

  useEffect(() => { setTab(initial); }, [initial]);

  const selectTab = useCallback((next) => {
    if (next === tab) return;
    hapticTick();
    setTab(next);
    // Keep the URL truthful without adding history entries for a sub-tab flick.
    if (history && history.replace) {
      history.replace(next === 'loyalty' ? '/dashboard/loyalty' : '/dashboard/wallet');
    }
  }, [tab, history]);

  // Axis-locked horizontal swipe between the two panes. Gestures starting
  // inside a horizontally scrollable element belong to that element.
  const startedInsideHorizontalScroller = (target, boundary) => {
    let el = target;
    while (el && el !== boundary) {
      if (el.scrollWidth > el.clientWidth + 1) {
        const overflowX = getComputedStyle(el).overflowX;
        if (overflowX === 'auto' || overflowX === 'scroll') return true;
      }
      el = el.parentElement;
    }
    return false;
  };

  const onTouchStart = (e) => {
    if (startedInsideHorizontalScroller(e.target, e.currentTarget)) {
      touchRef.current.active = false;
      return;
    }
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, axis: null, active: true };
  };

  const onTouchMove = (e) => {
    const ref = touchRef.current;
    if (!ref.active) return;
    const t = e.touches[0];
    const dx = t.clientX - ref.x;
    const dy = t.clientY - ref.y;
    if (!ref.axis) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      ref.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (ref.axis !== 'x') return;
    const idx = TABS.indexOf(tab);
    const atEdge = (dx > 0 && idx === 0) || (dx < 0 && idx === TABS.length - 1);
    setDragX(dx * (atEdge ? 0.12 : 0.35));
  };

  const onTouchEnd = () => {
    const ref = touchRef.current;
    if (!ref.active) return;
    ref.active = false;
    const drag = dragX;
    setDragX(0);
    if (ref.axis !== 'x') return;
    const COMMIT = 24;
    const idx = TABS.indexOf(tab);
    if (drag <= -COMMIT && idx < TABS.length - 1) selectTab(TABS[idx + 1]);
    else if (drag >= COMMIT && idx > 0) selectTab(TABS[idx - 1]);
  };

  return (
    <div className="dashboard-fade-in wallet-page wallet-hub">
      <div className="dashboard-page-header">
        <div className="dashboard-page-header-text">
          <h1 className="dashboard-page-title">Wallet</h1>
          <p className="dashboard-page-subtitle">Gift cards and loyalty, all in one place</p>
        </div>
      </div>

      <div className="wallet-hub-tabs" role="tablist" aria-label="Wallet sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'wallet'}
          className={`wallet-hub-tab ${tab === 'wallet' ? 'is-active' : ''}`}
          onClick={() => selectTab('wallet')}
        >
          Wallet
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'loyalty'}
          className={`wallet-hub-tab ${tab === 'loyalty' ? 'is-active' : ''}`}
          onClick={() => selectTab('loyalty')}
        >
          Loyalty
        </button>
      </div>

      <div
        className="wallet-hub-pane"
        style={dragX !== 0 ? { transform: `translateX(${dragX}px)`, transition: 'none' } : undefined}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {tab === 'wallet'
          ? <Wallet history={history} embedded />
          : <LoyaltyWallet history={history} embedded />}
      </div>
    </div>
  );
};

export default WalletHub;
